import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Location } from "react-router-dom";

import ExpandInIcon from "assets/icons/ExpandInIcon";
import ExpandOutIcon from "assets/icons/ExpandOutIcon";
import CancelIcon from "assets/icons/CancelIcon";
import HomeIcon from "assets/icons/HomeIcon";
import PhotoIcon from "assets/icons/PhotoIcon";
import ClassIcon from "assets/icons/ClassIcon";
import AccountIcon from "assets/icons/AccountIcon";
import NotificationIcon from "assets/icons/NotificationIcon";
import KidIcon from "assets/icons/KidIcon";
import SettingIcon from "assets/icons/SettingIcon";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import LeaveIcon from "assets/icons/LeaveIcon";
import NavbarItem from "components/__reusable/NavbarItem";
import useWindowDimensions from "hooks/useWindowDimensions";
import {
  BASE_PATH,
  PICTURES_PATH,
  CLASS_PATH,
  ACCOUNT_PATH,
  SYSTEM_INFO_PATH,
  FACILITY_PATH,
  SETTING_PATH,
  LOGIN_PATH,
  PICTURES_SUMMARIZATION_PATH,
} from "router/pathName";
import { OPEN_NAV } from "config/keywords";
import LanguageDropdown from "components/__reusable/LanguageDropdown";
import useIsPrincipal from "hooks/useIsPrincipal";
import useAppDispatch from "hooks/useAppDispatch";
import { reset } from "features/pictureSearch/pictureSearchSlice";

// Constants
const MOBILE_BREAKPOINT = 1025;
const NAVBAR_CLASSES = {
  base: [
    "bg-white",
    "h-screen",
    "flex",
    "flex-col",
    "justify-between",
    "transition-all",
    "ease-in-out",
    "duration-300",
    "lg:min-w-[5%]",
    "shadow-lg",
    "relative",
    "z-50",
  ],
  closed: ["w-0", "lg:w-16", "overflow-hidden"],
  open: ["px-3", "py-5", "w-full", "lg:w-64"],
};

// Types
interface NavItemConfig {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
  isActive?: (location: Location) => boolean;
}

const Navbar = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const isPrincipal = useIsPrincipal();
  const navRef = useRef<HTMLElement>(null);

  const [isClosed, setIsClosed] = useState(false);
  const [isContactInfoOpen, setIsContactInfoOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const isMobileView = useMemo(() => width < MOBILE_BREAKPOINT, [width]);

  // Initialize navbar state based on viewport
  useEffect(() => {
    setIsClosed(isMobileView);
  }, [isMobileView]);

  // Close contact info when navbar is closed
  useEffect(() => {
    if (isClosed) {
      setIsContactInfoOpen(false);
    }
  }, [isClosed]);

  useEffect(() => {
    const sidebarWidth = isMobileView ? "0px" : isClosed ? "4rem" : "16rem";
    document.documentElement.style.setProperty(
      "--app-sidebar-width",
      sidebarWidth,
    );

    return () => {
      document.documentElement.style.removeProperty("--app-sidebar-width");
    };
  }, [isClosed, isMobileView]);

  // Handle click outside on mobile
  useEffect(() => {
    if (!isMobileView || isClosed) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsClosed(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileView, isClosed]);

  // Keyboard navigation - ESC to close on mobile
  useEffect(() => {
    if (!isMobileView || isClosed) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsClosed(true);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileView, isClosed]);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (isMobileView && !isClosed) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileView, isClosed]);

  const handleOpenNavbar = useCallback(() => {
    setIsClosed(false);
  }, []);

  const handleToggleNavbar = useCallback(() => {
    setIsClosed((prev) => !prev);
  }, []);

  const handleCloseNavbar = useCallback(() => {
    setIsClosed(true);
  }, []);

  const handleToggleContactInfo = useCallback(() => {
    if (!isClosed) {
      setIsContactInfoOpen((prev) => !prev);
    }
  }, [isClosed]);

  const handleNavItemClick = useCallback(() => {
    dispatch(reset());
    if (isMobileView) {
      setIsClosed(true);
    }
  }, [dispatch, isMobileView]);

  useEffect(() => {
    window.addEventListener(OPEN_NAV, handleOpenNavbar);
    return () => {
      window.removeEventListener(OPEN_NAV, handleOpenNavbar);
    };
  }, [handleOpenNavbar]);

  const navbarClassName = useMemo(() => {
    const classes = [...NAVBAR_CLASSES.base];
    if (isClosed) {
      classes.push(...NAVBAR_CLASSES.closed);
    } else {
      classes.push(...NAVBAR_CLASSES.open);
    }
    return classes.join(" ");
  }, [isClosed]);

  const primaryNavItems: NavItemConfig[] = useMemo(
    () => [
      {
        path: BASE_PATH,
        labelKey: "_navigation._home",
        icon: <HomeIcon width="100%" height="100%" color="#767676" />,
        isActive: (location) =>
          location.pathname === BASE_PATH ||
          location.pathname === PICTURES_SUMMARIZATION_PATH,
      },
      {
        path: PICTURES_PATH,
        labelKey: "_navigation._picture_management",
        icon: <PhotoIcon width="100%" height="100%" color="#767676" />,
      },
      {
        path: CLASS_PATH,
        labelKey: "_navigation._class_management",
        icon: <ClassIcon width="100%" height="100%" color="#767676" />,
      },
      {
        path: ACCOUNT_PATH,
        labelKey: "_navigation._account_management",
        icon: <AccountIcon width="100%" height="100%" color="#767676" />,
      },
    ],
    [],
  );

  const secondaryNavItems: NavItemConfig[] = useMemo(() => {
    const items: NavItemConfig[] = [
      {
        path: SYSTEM_INFO_PATH,
        labelKey: "_navigation._notification",
        icon: <NotificationIcon width="100%" height="100%" color="#767676" />,
      },
    ];

    if (isPrincipal) {
      items.push({
        path: FACILITY_PATH,
        labelKey: "_navigation._facility_information",
        icon: <KidIcon width="100%" height="100%" color="#767676" />,
      });
    }

    items.push({
      path: SETTING_PATH,
      labelKey: "_navigation._setting",
      icon: <SettingIcon width="100%" height="100%" color="#767676" />,
    });

    return items;
  }, [isPrincipal]);

  // Tooltip component for collapsed state
  const Tooltip = ({ text, show }: { text: string; show: boolean }) => {
    if (!show || !isClosed || isMobileView) return null;

    return (
      <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-md whitespace-nowrap z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        {text}
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
      </div>
    );
  };

  // Component renderers
  const renderNavHeader = () => (
    <div
      className={`flex items-center ${isClosed ? "mt-2 justify-center gap-0" : "justify-between gap-3"} transition-all duration-300`}
    >
      <div
        className={`transition-all duration-300 ease-in-out ${
          isClosed
            ? "h-0 w-0 opacity-0 overflow-hidden"
            : "flex h-[72px] w-[230px] max-w-[calc(100%-3rem)] items-center overflow-hidden opacity-100"
        }`}
      >
        <img
          className="h-full w-auto object-contain object-left"
          src="/images/irodoki_logo_with_mark.png"
          alt="logo"
        />
      </div>
      <button
        className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors duration-150"
        onClick={handleToggleNavbar}
        aria-label={
          isClosed
            ? t("_navigation._expand_menu")
            : t("_navigation._collapse_menu")
        }
      >
        <div className="transition-transform duration-300 hover:scale-110">
          {isClosed ? (
            <ExpandInIcon width={24} height={24} color="#767676" />
          ) : (
            <ExpandOutIcon width={24} height={24} color="#767676" />
          )}
        </div>
      </button>
      <button
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors duration-150 lg:hidden"
        onClick={handleCloseNavbar}
        aria-label={t("_navigation._close_menu")}
      >
        <CancelIcon width={28} height={28} color="#767676" />
      </button>
    </div>
  );

  const renderNavItem = (config: NavItemConfig) => (
    <div
      key={config.path}
      className="relative group"
      onMouseEnter={() => setHoveredItem(config.path)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <NavbarItem
        link={config.path}
        icon={config.icon}
        label={!isClosed ? t(config.labelKey) : null}
        onClick={handleNavItemClick}
        isActive={config.isActive}
      />
      <Tooltip text={t(config.labelKey)} show={hoveredItem === config.path} />
    </div>
  );

  const renderContactInfo = () => {
    if (isClosed) return null;

    return (
      <div
        className={`flex flex-col space-y-3 transition-all duration-300 ${
          isClosed ? "opacity-0" : "opacity-100"
        }`}
        role="region"
        aria-label={t("_navigation._contact_us")}
      >
        <button
          className="flex cursor-pointer items-center space-x-2 text-left text-sm font-semibold group"
          onClick={handleToggleContactInfo}
          aria-expanded={isContactInfoOpen}
          aria-controls="contact-info-content"
        >
          <span className="rounded p-1 transition-all duration-150 group-hover:bg-gray-100 group-active:bg-gray-200">
            <div
              className={`transition-transform duration-300 ${isContactInfoOpen ? "rotate-0" : "-rotate-90"}`}
            >
              <ArrowDownIcon width={16} height={16} color="#555555" />
            </div>
          </span>
          <span className="group-hover:text-gray-700 transition-colors duration-150">
            {t("_navigation._contact_us")}
          </span>
        </button>
        <div
          id="contact-info-content"
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isContactInfoOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="pl-8">
            <ul className="space-y-1.5">
              <li className="text-[#767676] text-sm">
                {t("_navigation._sakura_company")}
              </li>
              <li className="text-[#767676] text-sm">
                {t("_navigation._irodoki_support_office")}
              </li>
              <li className="text-[#767676] text-sm">
                {t("_navigation._email")}：
                <a
                  href="mailto:support@irodoki.com"
                  className="hover:underline focus:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded transition-all duration-150"
                >
                  support@irodoki.com
                </a>
              </li>
              <li className="text-[#767676] text-sm">
                <a
                  href="tel:06-4967-1283"
                  className="hover:underline focus:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded transition-all duration-150"
                >
                  TEL:06-4967-1283
                </a>
              </li>
              <li className="text-[#767676] text-sm">
                {t("_navigation._working_hour")}
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderDivider = () => (
    <div
      className="h-px bg-gradient-to-r from-transparent via-[#E7E5E0] to-transparent"
      role="separator"
    />
  );

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobileView && !isClosed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={handleCloseNavbar}
          aria-hidden="true"
        />
      )}

      <nav
        ref={navRef}
        className={navbarClassName}
        role="navigation"
        aria-label="Main navigation"
      >
        {renderNavHeader()}
        <div className="h-full flex flex-col justify-between overflow-y-auto overflow-x-hidden custom-scrollbar">
          <div className={`flex-1 space-y-4 ${isClosed ? "" : "pt-4"}`}>
            <div className="space-y-1">
              {primaryNavItems.map(renderNavItem)}
            </div>
            {renderDivider()}
            <div className="space-y-1">
              {secondaryNavItems.map(renderNavItem)}
            </div>
            {renderDivider()}
            {renderContactInfo()}
          </div>

          <div className="space-y-3 pt-4 border-t border-gray-100">
            {!isClosed && (
              <div
                className="lg:hidden w-full opacity-0 animate-fadeIn"
                style={{
                  animationDelay: "150ms",
                  animationFillMode: "forwards",
                }}
              >
                <LanguageDropdown />
              </div>
            )}
            <div className="relative group">
              <NavbarItem
                link={LOGIN_PATH}
                icon={<LeaveIcon width="100%" height="100%" color="#ff0000" />}
                label={!isClosed ? t("_navigation._logout") : null}
                onClick={handleNavItemClick}
                compact
              />
              <Tooltip
                text={t("_navigation._logout")}
                show={hoveredItem === "logout"}
              />
            </div>
          </div>
        </div>
      </nav>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default Navbar;
